/**
 * Campaign create/edit, delete, membership and the "Add to Campaign" picker
 * leads reach this module through — same shapes ContactModals.jsx/
 * OpportunityModals.jsx use (plain state forms, a shared `Field`, a small
 * local `Picker` select), not React Hook Form: this codebase's actual
 * modals never adopted RHF despite it being on `CLAUDE.md`'s target stack
 * list, and matching what's already here beats a one-off exception.
 */
import { useEffect, useState } from 'react';
import {
  Modal, Field, Badge, Avatar, Spinner, ErrorState, EmptyState,
} from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import {
  useCampaignFormOptions, useCampaignPickerOptions, useCampaignMembers,
  useCreateCampaign, useAddCampaignMembers, useRemoveCampaignMember,
} from './useCampaigns.js';
import { CAMPAIGN_STATUS, STATUS_LABEL } from './campaignShape.js';

const Picker = ({ value, onChange, options, blank = 'Not set', loading }) => (
  <select className="form-select" value={value} onChange={onChange} disabled={loading}>
    <option value="">{loading ? 'Loading…' : blank}</option>
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

const BLANK = {
  code: '', name: '', campaign_type_id: '', status: 'planned',
  start_date: '', end_date: '', budget: '', actual_cost: '', currency: '',
  owner_id: '', target_audience: '',
};

function validate(form) {
  const errors = {};
  if (!form.code.trim()) errors.code = 'Required.';
  if (!form.name.trim()) errors.name = 'Required.';
  if (form.start_date && form.end_date && form.end_date < form.start_date) {
    errors.end_date = 'Must be on or after the start date.';
  }
  return errors;
}

/** Create/edit — `campaign` is the row from `campaignShape.toRow`, or null for a new one. */
export function CampaignFormModal({ open, campaign, onClose, onSubmit, busy }) {
  const options = useCampaignFormOptions();
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const editing = Boolean(campaign);

  useEffect(() => {
    if (!open) return;
    if (campaign) {
      const c = campaign.raw;
      setForm({
        code: c.code ?? '', name: c.name ?? '', campaign_type_id: c.campaign_type?.id ?? '',
        status: c.status ?? 'planned', start_date: c.start_date ?? '', end_date: c.end_date ?? '',
        budget: c.budget ?? '', actual_cost: c.actual_cost ?? '', currency: c.currency ?? '',
        owner_id: c.owner?.id ?? '', target_audience: c.target_audience ?? '',
      });
    } else {
      setForm(BLANK);
    }
    setErrors({});
  }, [open, campaign]);

  const set = (k) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [k]: value }));
    setErrors((er) => (er[k] ? { ...er, [k]: undefined } : er));
  };

  const submit = (e) => {
    e.preventDefault();
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const payload = {};
    for (const [k, v] of Object.entries(form)) {
      if (v === '' || v == null) { if (editing) payload[k] = null; continue; }
      if (k === 'budget' || k === 'actual_cost') { payload[k] = Number(v); continue; }
      payload[k] = v;
    }
    /* code/name stay required, never sent as null even when "cleared" — the
       DB column is NOT NULL and the form already refuses to submit blank. */
    payload.code = form.code.trim();
    payload.name = form.name.trim();
    onSubmit(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Campaign' : 'New Campaign'}
      size="lg"
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="submit" form="campaignForm" className="btn btn-save btn-sm-x" disabled={busy}>
            {busy ? <><ButtonSpinner />Saving…</> : editing ? 'Save changes' : 'Save campaign'}
          </button>
        </>
      )}
    >
      <form id="campaignForm" onSubmit={submit} noValidate>
        <div className="row g-3 mb-3">
          <div className="col-md-4">
            <Field label="Code" required error={errors.code} help="A short unique identifier, e.g. Q4-WEBINAR.">
              <input className="form-control" value={form.code} onChange={set('code')} />
            </Field>
          </div>
          <div className="col-md-8">
            <Field label="Name" required error={errors.name}>
              <input className="form-control" value={form.name} onChange={set('name')} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Type">
              <Picker value={form.campaign_type_id} onChange={set('campaign_type_id')} options={options.types} blank="Not set" loading={options.loading} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Status">
              <select className="form-select" value={form.status} onChange={set('status')}>
                {CAMPAIGN_STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Owner">
              <Picker value={form.owner_id} onChange={set('owner_id')} options={options.owners} blank="Unassigned" loading={options.loading} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Start date"><input type="date" className="form-control" value={form.start_date} onChange={set('start_date')} /></Field>
          </div>
          <div className="col-md-4">
            <Field label="End date" error={errors.end_date}>
              <input type="date" className="form-control" value={form.end_date} onChange={set('end_date')} />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Currency" help="Three-letter code, e.g. USD."><input className="form-control" maxLength={3} value={form.currency} onChange={(e) => set('currency')({ target: { value: e.target.value.toUpperCase() } })} /></Field>
          </div>
          <div className="col-md-6">
            <Field label="Budget"><input type="number" min="0" step="0.01" className="form-control" value={form.budget} onChange={set('budget')} /></Field>
          </div>
          <div className="col-md-6">
            <Field label="Actual cost" help="What's been spent so far — drives ROI and cost-per-lead."><input type="number" min="0" step="0.01" className="form-control" value={form.actual_cost} onChange={set('actual_cost')} /></Field>
          </div>
          <div className="col-12">
            <Field label="Target audience"><textarea className="form-control" rows={2} value={form.target_audience} onChange={set('target_audience')} /></Field>
          </div>
        </div>
      </form>
    </Modal>
  );
}

/** Delete, with the reason the database insists on. */
export function DeleteCampaignModal({ open, campaign, onClose, onConfirm, busy }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  if (!campaign) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Delete campaign"
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button
            type="button" className="btn btn-delete btn-sm-x" disabled={!reason.trim() || busy}
            onClick={() => onConfirm(reason.trim())}
          >
            {busy ? <><ButtonSpinner />Deleting…</> : 'Delete'}
          </button>
        </>
      )}
    >
      <p className="t-sm ink-2">
        Delete <strong>{campaign.name}</strong>? This also removes every activity logged against it.
      </p>
      <Field label="Reason" required>
        <input className="form-control" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Duplicate entry" autoFocus />
      </Field>
    </Modal>
  );
}

/**
 * Who's in the campaign. Adding happens from the record being added (a
 * lead's own "Add to Campaign" action, below) rather than a picker inside
 * this modal — searching leads/contacts from scratch here would duplicate
 * the leads screen's own search instead of reusing it.
 */
export function CampaignMembersModal({ open, campaign, onClose }) {
  const { data, isLoading, error, refetch } = useCampaignMembers(campaign?.id, {}, open);
  const removeMember = useRemoveCampaignMember();
  const rows = data?.data ?? [];

  if (!campaign) return null;

  return (
    <Modal open={open} onClose={onClose} title={`Members — ${campaign.name}`} size="lg">
      {isLoading ? <Spinner /> : error ? <ErrorState error={error} onRetry={refetch} /> : rows.length === 0 ? (
        <EmptyState icon="bi-people" title="No members yet" message="Add leads to this campaign from the Leads screen's Add to Campaign action." />
      ) : (
        <ul className="list-x">
          {rows.map((m) => {
            const who = m.lead ?? m.contact;
            return (
              <li key={m.id} className="list-x-item">
                <Avatar name={who?.full_name ?? '?'} size={30} />
                <div className="flex-grow-1 min-w-0">
                  <div className="fw-600 t-sm text-truncate">{who?.full_name ?? 'Unknown'}</div>
                  <div className="t-2xs ink-4">{m.lead ? m.lead.lead_no : m.contact?.contact_no}</div>
                </div>
                <Badge tone="neutral">{m.status}</Badge>
                <button
                  type="button" className="icon-btn" style={{ width: 28, height: 28, fontSize: 13 }}
                  title="Remove from campaign" disabled={removeMember.isPending}
                  onClick={() => removeMember.mutate({ campaignId: campaign.id, memberId: m.id })}
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}

/**
 * The real "Add to Campaign" flow — replaces the `NOT_BUILT.campaignAdd`
 * explainer on Leads now that the backend exists. `leadIds` is whatever the
 * caller has selected: one id from a lead's own record page, or several from
 * the list's bulk bar.
 */
export function AddToCampaignModal({ open, leadIds = [], onClose }) {
  const [mode, setMode] = useState('pick');
  const [campaignId, setCampaignId] = useState('');
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (!open) return;
    setMode('pick');
    setCampaignId('');
    setNewCode('');
    setNewName('');
  }, [open]);

  const { data, isLoading } = useCampaignPickerOptions();
  const campaigns = data?.data ?? [];
  const createCampaign = useCreateCampaign();
  const addMembers = useAddCampaignMembers();
  const busy = createCampaign.isPending || addMembers.isPending;
  const noLeads = leadIds.length === 0;

  const addTo = (id) => addMembers.mutate({ id, payload: { lead_ids: leadIds } }, { onSuccess: onClose });

  const confirm = async () => {
    if (noLeads) return;
    if (mode === 'pick') {
      if (campaignId) addTo(campaignId);
      return;
    }
    if (!newCode.trim() || !newName.trim()) return;
    const created = await createCampaign.mutateAsync({ code: newCode.trim(), name: newName.trim() });
    addTo(created.id);
  };

  const canConfirm = !noLeads && !busy && (mode === 'pick' ? Boolean(campaignId) : Boolean(newCode.trim() && newName.trim()));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add to Campaign"
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-save btn-sm-x" disabled={!canConfirm} onClick={confirm}>
            {busy ? <><ButtonSpinner />Adding…</> : `Add ${leadIds.length || ''} to campaign`}
          </button>
        </>
      )}
    >
      {noLeads ? (
        <div className="t-sm ink-3">Select one or more leads first, then use Add to Campaign again.</div>
      ) : (
        <>
          <div className="t-sm ink-3 mb-3">{leadIds.length} lead{leadIds.length === 1 ? '' : 's'} selected.</div>
          <div className="seg mb-3">
            <button type="button" className={mode === 'pick' ? 'active' : ''} onClick={() => setMode('pick')}>Existing campaign</button>
            <button type="button" className={mode === 'create' ? 'active' : ''} onClick={() => setMode('create')}>New campaign</button>
          </div>

          {mode === 'pick' ? (
            <Field label="Campaign" required>
              <select className="form-select" value={campaignId} onChange={(e) => setCampaignId(e.target.value)} disabled={isLoading}>
                <option value="">{isLoading ? 'Loading…' : 'Choose a campaign'}</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.code}) — {STATUS_LABEL[c.status] ?? c.status}</option>
                ))}
              </select>
            </Field>
          ) : (
            <div className="row g-3">
              <div className="col-md-4">
                <Field label="Code" required><input className="form-control" value={newCode} onChange={(e) => setNewCode(e.target.value)} /></Field>
              </div>
              <div className="col-md-8">
                <Field label="Name" required><input className="form-control" value={newName} onChange={(e) => setNewName(e.target.value)} /></Field>
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
